import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import {
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Sparkles,
  Wand2,
  MessageSquare,
  Mail
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplatesPage() {
  const { tenant, getToken } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const dashData = useDashboardData();
  const preloaded = dashData?.templates;
  const [templates, setTemplates] = useState(preloaded?.templates || []);
  const [loading, setLoading] = useState(!preloaded);
  const [error, setError] = useState(null);
  const skipInitialFetch = useRef(!!preloaded);

  // Channel filter
  const [channelFilter, setChannelFilter] = useState('');

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', message_body: '', channel: 'sms' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete Confirmation
  const [deleteId, setDeleteId] = useState(null);

  // AI Helper State
  const [aiPrompt, setAiPrompt] = useState({ promotion: '', tone: 'friendly' });
  const [includeBusinessName, setIncludeBusinessName] = useState(true);
  const [businessNameOverride, setBusinessNameOverride] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Auto-open new template modal when arriving from campaign page (?new=1)
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setIsModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (channelFilter) params.set('channel', channelFilter);
      const response = await fetch(`${BACKEND_URL}/api/templates?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      const data = await response.json();
      setTemplates(data.templates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, channelFilter]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return;
    }
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenModal = (template = null) => {
    if (template) {
      setCurrentTemplate(template);
      setFormData({ name: template.name, message_body: template.message_body, channel: template.channel || 'sms' });
    } else {
      setCurrentTemplate(null);
      setFormData({ name: '', message_body: '', channel: 'sms' });
    }
    setAiSuggestions([]);
    setAiError(null);
    setAiPrompt({ promotion: '', tone: 'friendly' });
    setIncludeBusinessName(true);
    setBusinessNameOverride('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentTemplate(null);
    setFormData({ name: '', message_body: '', channel: 'sms' });
    setAiSuggestions([]);
    setAiError(null);
    setError(null);
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.promotion.trim()) return;
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions([]);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/templates/ai-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          promotion: aiPrompt.promotion,
          tone: aiPrompt.tone,
          include_business_name: includeBusinessName,
          business_name_override: businessNameOverride || undefined
        })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to generate suggestions');
      }
      const data = await response.json();
      setAiSuggestions(data.suggestions || []);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseSuggestion = (suggestion) => {
    setFormData({ ...formData, name: suggestion.name, message_body: suggestion.message_body });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getToken();
      const url = currentTemplate
        ? `${BACKEND_URL}/api/templates/${currentTemplate.id}`
        : `${BACKEND_URL}/api/templates`;

      const method = currentTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save template');
      }

      fetchTemplates();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/templates/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete template');

      setTemplates(templates.filter(t => t.id !== id));
      setDeleteId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const SMS_OPT_OUT_SUFFIX = "\nOpt out: Reply STOP";

  const calculateSegments = (text) => {
    if (!text) return { chars: 0, totalChars: 0, segments: 0, isUnicode: false };
    const fullMessage = text.toLowerCase().includes('opt out: reply stop') ? text : text + SMS_OPT_OUT_SUFFIX;
    const isUnicode = /[^\u0000-\u00ff]/.test(fullMessage);
    const length = fullMessage.length;
    let segments;
    if (isUnicode) {
      segments = length <= 70 ? 1 : Math.ceil((length + 66) / 67);
    } else {
      segments = length <= 160 ? 1 : Math.ceil((length + 152) / 153);
    }
    return { chars: text.length, totalChars: length, segments, isUnicode };
  };

  const messageRef = useRef(null);

  const insertTag = (tag) => {
    const textarea = messageRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = formData.message_body.slice(0, start);
    const after = formData.message_body.slice(end);
    const newValue = before + tag + after;
    setFormData({ ...formData, message_body: newValue });
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + tag.length;
    });
  };

  const segmentInfo = calculateSegments(formData.message_body);
  const segmentCount = segmentInfo.segments;

  return (
    <div className="p-6 lg:p-8" data-testid="templates-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaign Templates</h1>
          <p className="text-gray-600 mt-1">Save message templates for quick reuse</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={channelFilter}
            onChange={(e) => setChannelFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white"
          >
            <option value="">All Channels</option>
            <option value="sms">SMS Only</option>
            <option value="email">Email Only</option>
          </select>
          <OnboardingTooltip id="templates_create" text="Create reusable templates — try our AI writer!">
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </OnboardingTooltip>
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-3"><Skeleton className="h-5 w-32" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-3/4" /><Skeleton className="h-3 w-20" /></div>)}
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-500 mb-4">Create your first message template to save time.</p>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <Plus className="w-4 h-4" />
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => (
            <div key={template.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                    template.channel === 'email'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {template.channel === 'email' ? <Mail className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                    {template.channel === 'email' ? 'Email' : 'SMS'}
                  </span>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleOpenModal(template)}
                    className="p-1 text-gray-400 hover:text-blue-600 rounded"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteId(template.id)}
                    className="p-1 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 bg-gray-50 rounded-lg p-3 text-sm text-gray-600 mb-4 overflow-hidden line-clamp-4 font-mono">
                {template.message_body}
              </div>

              <div className="text-xs text-gray-400 mt-auto">
                Updated {new Date(template.updated_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onKeyDown={(e) => { if (e.key === 'Escape') handleCloseModal(); }} onClick={(e) => { if (e.target === e.currentTarget) handleCloseModal(); }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" tabIndex={-1}>
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-lg font-semibold">{currentTemplate ? 'Edit Template' : 'New Template'}</h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Channel Selector */}
            <div className="px-6 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Channel</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, channel: 'sms' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.channel === 'sms'
                      ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  SMS
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, channel: 'email' })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.channel === 'email'
                      ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-300'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </button>
              </div>
            </div>

            {/* AI Assistant — only for new SMS templates */}
            {!currentTemplate && formData.channel === 'sms' && (
              <div className="p-6 border-b bg-gradient-to-b from-orange-50/50 to-white">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-semibold text-gray-800">Need help writing a message?</span>
                </div>
                <p className="text-xs text-gray-500 mb-4">Tell us what you'd like to send and we'll write it for you.</p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">What would you like to tell your customers?</label>
                    <input
                      type="text"
                      value={aiPrompt.promotion}
                      onChange={(e) => setAiPrompt({ ...aiPrompt, promotion: e.target.value })}
                      placeholder="e.g. 20% off all large pizzas this weekend only"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      maxLength={500}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">How should it sound?</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'friendly', label: 'Casual & Friendly' },
                        { value: 'professional', label: 'Professional' },
                        { value: 'urgent', label: 'Urgent / FOMO' },
                      ].map(({ value, label }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setAiPrompt({ ...aiPrompt, tone: value })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                            aiPrompt.tone === value
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Business Name Toggle */}
                  <div className="p-3 bg-white border border-gray-200 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-gray-600">Include your shop name in message?</label>
                      <button
                        type="button"
                        onClick={() => setIncludeBusinessName(!includeBusinessName)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${includeBusinessName ? 'bg-orange-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${includeBusinessName ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                    {includeBusinessName && (
                      <div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={businessNameOverride || tenant?.business?.name || ''}
                            onChange={(e) => setBusinessNameOverride(e.target.value)}
                            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                        {!businessNameOverride && tenant?.business?.name && (
                          <p className="text-[11px] text-gray-400 mt-1">This is your registered business name. Edit it here if you'd like something different.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={aiLoading || !aiPrompt.promotion.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                  >
                    {aiLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4" />
                    )}
                    {aiLoading ? 'Writing messages...' : 'Write it for me'}
                  </button>

                  {aiError && (
                    <div className="bg-red-50 text-red-700 p-3 rounded-lg text-xs flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      {aiError}
                    </div>
                  )}

                  {aiSuggestions.length > 0 && (
                    <div className="space-y-2 pt-1">
                      {aiSuggestions.map((s, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex justify-between items-center mb-1.5">
                            <span className="text-xs font-medium text-gray-700">{s.name}</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              s.credits <= 1
                                ? 'bg-green-100 text-green-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {s.credits} SMS per recipient
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 font-mono mb-2">{s.message_body}</p>
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">{s.char_count} chars ({s.total_with_optout} with opt-out)</span>
                            <button
                              type="button"
                              onClick={() => handleUseSuggestion(s)}
                              className="text-xs font-medium text-orange-600 hover:text-orange-700 px-2 py-1 rounded hover:bg-orange-50 transition-colors"
                            >
                              Use This
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Welcome Message"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.channel === 'email' ? 'Email Content' : 'Message Content'}
                </label>
                {formData.channel === 'sms' && (
                  <div className="flex gap-1.5 mb-2">
                    {[
                      { label: 'First Name', tag: '{first_name}' },
                      { label: 'Last Name', tag: '{last_name}' },
                      { label: 'Business Name', tag: '{business_name}' },
                    ].map(({ label, tag }) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => insertTag(tag)}
                        className="px-2 py-1 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <textarea
                  ref={messageRef}
                  required
                  rows={formData.channel === 'email' ? 8 : 5}
                  value={formData.message_body}
                  onChange={(e) => setFormData({ ...formData, message_body: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-sm"
                  placeholder={formData.channel === 'email' ? 'Type your email content here...' : 'Type your message here...'}
                />
                {formData.channel === 'sms' && (
                  <>
                    <div className="flex justify-end mt-1 text-xs text-gray-500">
                      {segmentInfo.chars} chars ({segmentInfo.totalChars} with opt-out) • {segmentCount} SMS per recipient
                    </div>
                    {segmentInfo.isUnicode && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                        <p className="font-semibold">Emojis or special characters detected — this costs more!</p>
                        <p className="text-xs mt-1">
                          Standard SMS: 160 chars = 1 SMS. With emojis: 70 chars = 1 SMS. Remove emojis to reduce costs.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onKeyDown={(e) => { if (e.key === 'Escape') setDeleteId(null); }} onClick={(e) => { if (e.target === e.currentTarget) setDeleteId(null); }}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center" tabIndex={-1}>
             <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
               <Trash2 className="w-6 h-6 text-red-600" />
             </div>
             <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Template?</h3>
             <p className="text-gray-500 mb-6">Are you sure you want to delete this template? This action cannot be undone.</p>
             <div className="flex gap-3 justify-center">
               <button
                 onClick={() => setDeleteId(null)}
                 className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
               >
                 Cancel
               </button>
               <button
                 onClick={() => handleDelete(deleteId)}
                 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
               >
                 Delete
               </button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
}
